// ============================================================
// HTML PPT 管理 API 封装（eltmax 内容管理平台）
// ============================================================
import http from '@/utils/http'

// ---------- PPT 项目（导入转换 / 查询 / 维护） ----------
// 后端：com.eltmax.eduManage.controller.PptProjectController（JSON body / 路径参数）
export const getPptProjectPage = (params) => http.post('/api/edu/ppt/project/getPptProjectPage', params)
export const addPptProject = (data) => http.post('/api/edu/ppt/project/addPptProject', data)
export const updatePptProject = (data) => http.post('/api/edu/ppt/project/updatePptProject', data)
export const getPptProjectById = (id) => http.get(`/api/edu/ppt/project/getPptProjectById/${id}`)
export const deletePptProject = (id) => http.post(`/api/edu/ppt/project/deletePptProject/${id}`)

// ---------- PowerPoint 转换（导入 .pptx → 转换引擎 → 落库草稿） ----------
// 后端：com.eltmax.eduManage.controller.PptConvertController（multipart：file / title）
// 返回：Result{ code:0, data: { id, title, themeJson, pagesJson, ... } }
export function convertPptx(file, title) {
  const fd = new FormData()
  fd.append('file', file)
  if (title) fd.append('title', title)
  return http.post('/api/edu/ppt/convert/convertPptx', fd, { timeout: 1800000 })
}

// 「复杂PPT转换」：逐页分类（普通页整页渲染 / 视频页提取内嵌视频 / 动画页关键帧序列）
// 返回：Result{ code:0, data: { id, title, themeJson, pagesJson(含type/anim/frames/videoUrl/videoBox), ... } }
// 前端据此进入 /pptconvert?id=xx 逐页确认
export function convertPptxSmart(file, title) {
  const fd = new FormData()
  fd.append('file', file)
  if (title) fd.append('title', title)
  return http.post('/api/edu/ppt/convert/convertPptxSmart', fd, { timeout: 1800000 })
}

// ---------- 素材上传：复用平台现有阿里云 OSS 接口（后端零改动） ----------
// 后端已有实现：com.eltmax.eduManage.controller.OssController
//   POST /edu/uploadOss/uploadImage 参数 file，仅 jpg/png/jpeg，自动压缩到 1000KB，
//        返回 BaseResult{code,message,data}，data = 带签名 OSS 直链（30 年有效）
//   POST /edu/uploadOss/uploadVideo 参数 video，仅 mp4，
//        返回 BaseResult{code,message,data}，data = 带签名 OSS 直链（15 年有效）
export function uploadPptImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return http.post('/api/edu/uploadOss/uploadImage', fd)
}
export function uploadPptVideo(file) {
  const fd = new FormData()
  fd.append('video', file)
  return http.post('/api/edu/uploadOss/uploadVideo', fd)
}

// ---------- 受保护导出：资源进私有桶 eltmax-ppt-assets，只返回资源编号(object key)，不做签名 ----------
// 播放时由 eOnline 登录鉴权后签临时URL，导出文件离开 eOnline 无法播放（防下载盗用）
// 后端：OssController
//   POST /edu/uploadOss/uploadPptAsset 参数 file（图片/音频/视频二进制），返回资源编号
//   POST /edu/uploadOss/importPptAsset 参数 url（现有OSS资源地址，服务器端拷贝），返回资源编号
export function uploadPptExportAsset(file) {
  const fd = new FormData()
  fd.append('file', file)
  return http.post('/api/edu/uploadOss/uploadPptAsset', fd, { timeout: 300000 })
}
export function importPptExportAsset(url) {
  const fd = new FormData()
  fd.append('url', url)
  return http.post('/api/edu/uploadOss/importPptAsset', fd, { timeout: 60000 })
}

// ---------- 补拍动画帧：真屏采集某页人工放映画面+内录音，替换该页 frames/frameT/audio ----------
// 适用「单击交互页」等自动转换出不来动画的页：用户在 PowerPoint 里手动放映并点击交互，
// 后端真屏连拍+内录；向导组件见 views/ppt-manage/pptedit/recapture-dialog.vue
// start/stop/commit 均返回 Result{code:0,data}，stop 的 data = {frames:[{i,t}], audio, durMs}
export function recaptureStart(projectId, page) { return http.post('/api/edu/ppt/recapture/start', { projectId, page }) }
export function recaptureStop(projectId, page) { return http.post('/api/edu/ppt/recapture/stop', { projectId, page }) }
// 帧图地址（仅用于 axios 请求拼 URL）：该接口走 Authorization 头鉴权，<img> 标签带不上请求头，
// 不能直接作 img 的 src，须在组件里用 http.get(url, null, true) 拉 arraybuffer → Blob → objectURL
export function recaptureFrameUrl(projectId, page, idx) { return `/api/edu/ppt/recapture/frame?projectId=${projectId}&page=${page}&idx=${idx}` }
// frames 为选中的帧序号数组（帧的 i 值），useAudio 为是否采用内录音
export function recaptureCommit(projectId, page, frames, useAudio) { return http.post('/api/edu/ppt/recapture/commit', { projectId, page, frames, useAudio }) }
// 一键采集：上传原始 pptx（首次，file 必传）后由后端 PowerPoint COM 自动打开并跳到指定页全屏放映；
// 同项目后续页传 file=null 复用后端缓存，不再重复上传。返回 Result{code:0,data:{running:true,page}}
export function recaptureOpenSlide(projectId, page, file) {
  const fd = new FormData()
  fd.append('projectId', projectId)
  fd.append('page', page)
  if (file) fd.append('file', file)
  return http.post('/api/edu/ppt/recapture/open-slide', fd, { timeout: 600000 })
}
// 后端构建/进程信息（转换页展示，识别"跑的是不是旧进程"）
export function getPptBuildInfo() { return http.get('/api/edu/ppt/convert/build-info') }
